import { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Calendar, Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompanyHolidays({ user, onLogout }) {
  const [holidays, setHolidays] = useState([]);
  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: '',
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  useEffect(() => {
    applySearch();
  }, [searchTerm, holidays]);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/holidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHolidays(response.data);
      setFilteredHolidays(response.data);
    } catch (error) {
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const applySearch = () => {
    if (!searchTerm.trim()) {
      setFilteredHolidays(holidays);
      return;
    }
    
    const filtered = holidays.filter(h =>
      h.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      h.date.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredHolidays(filtered);
  };

  const openAddModal = () => {
    setEditingHoliday(null);
    setFormData({ name: '', date: '', description: '' });
    setShowModal(true);
  };

  const openEditModal = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      description: holiday.description || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', description: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingHoliday) {
        // Update existing holiday
        await axios.put(`${API}/holidays/${editingHoliday.holiday_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Holiday updated successfully!');
      } else {
        // Create new holiday
        await axios.post(`${API}/holidays`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Holiday added successfully!');
      }
      
      closeModal();
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save holiday');
    }
  };

  const handleDeleteHoliday = async (holidayId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Holiday deleted successfully');
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete holiday');
    }
  };

  const formatDisplayDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="flex-1 md:ml-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-4 md:px-8 py-4 md:py-6 pt-16 md:pt-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">Company Holidays</h1>
              <p className="text-sm md:text-base text-muted-foreground mt-1">Manage company-wide holidays</p>
            </div>
            <button
              onClick={openAddModal}
              data-testid="add-holiday-button"
              className="flex items-center justify-center gap-2 px-4 md:px-5 py-2 md:py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-heading font-semibold shadow-sm text-sm md:text-base w-full md:w-auto"
            >
              <Plus className="w-4 h-4 md:w-5 md:h-5" />
              Add Holiday
            </button>
          </div>
        </div>

        <div className="p-4 md:p-8">
          {/* Search Bar */}
          <div className="bg-white rounded-xl border border-border p-4 md:p-6 mb-6 md:mb-8 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 md:w-5 h-4 md:h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search holidays..."
                  data-testid="search-holidays"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 md:pl-11 pr-4 py-2 md:py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="text-sm text-muted-foreground font-medium">
                {filteredHolidays.length} holidays
              </div>
            </div>
          </div>

          {/* Holidays List */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading holidays...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredHolidays.map((holiday) => (
                <div
                  key={holiday.holiday_id}
                  className="bg-white rounded-xl border border-border p-6 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`holiday-${holiday.holiday_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
                          {holiday.name}
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {formatDisplayDate(holiday.date)}
                        </p>
                        {holiday.description && (
                          <p className="text-sm text-muted-foreground">
                            {holiday.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => openEditModal(holiday)}
                        data-testid={`edit-holiday-${holiday.holiday_id}`}
                        className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteHoliday(holiday.holiday_id, holiday.name)}
                        data-testid={`delete-holiday-${holiday.holiday_id}`}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredHolidays.length === 0 && (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'No holidays found matching your search' : 'No holidays configured yet'}
              </p>
              {!searchTerm && (
                <button
                  onClick={openAddModal}
                  className="text-primary hover:underline font-medium"
                >
                  Add your first holiday
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Holiday Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={closeModal}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground">
                {editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Holiday Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  data-testid="holiday-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="e.g., Christmas Day"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  data-testid="holiday-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-2">
                  Description <span className="text-muted-foreground">(optional)</span>
                </label>
                <textarea
                  data-testid="holiday-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-input rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={3}
                  placeholder="Optional description or notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  data-testid="submit-holiday"
                  className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-lg px-6 py-3 font-heading font-semibold transition-colors"
                >
                  {editingHoliday ? 'Update Holiday' : 'Add Holiday'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 border border-input rounded-lg text-foreground hover:bg-muted transition-colors font-heading font-medium"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
